import { Button, Dropdown, Empty, Menu, Space, Tooltip } from 'antd'
import { globalToaster } from 'components/elements/global'
import { ADMIN_PREFIX_PATH } from 'config'
import { useLeadDetails } from 'features/leads/providers/LeadDetailsProvider'
import useAppControl from 'hooks/useAppControl'
import useAuth from 'hooks/useAuth'
import { useGlobal } from 'raducer/global'
import { FC, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

export const INTEGRATION_PATH = `${ADMIN_PREFIX_PATH}/integration`

// Shared website-link message templates (AR0569 - WEB Activity spec)
const websiteEmailPrefill = (link: string) => ({
 subject: 'Take a look at homes for sale, [[name]]',
 // Empty #signature block: handleSignatureContent only injects the user's
 // signature into a <body> or an existing #signature div, not plain <p> content
 body: `<p>Hi [[name]], here's a link to search available listings and get notified when new homes match what you're looking for: <a href="${link}" target="_blank" rel="noopener noreferrer">${link}</a>. Let me know if you have any questions</p><div id="signature"></div>`,
})
const websiteSmsPrefill = (link: string) => ({
 body: `Hi [[name]], here's a link to search available listings: ${link}. — [[realtor_name]]`,
})

/**
 * Capabilities + handlers for the lead timeline empty-state actions.
 * - SMS: available when the account has a CRM (smart) number, the SMS sender
 * - Call: available when Twilio voice is enabled
 * - Website link: the realtor's website from their profile
 */
export const useLeadActivityActions = () => {
 const { lead, onCall, isTransferred } = useLeadDetails()
 const { user } = useAuth()
 const { global } = useGlobal()
 const { updateSendEmailWidget, updateSendSMSWidget } = useAppControl()

 // Same rule as the lead speed dial "SMS from Smart Number"
 const canSms = !!user?.twilio_phone
 const canCall = global?.voice_enabled === true
 const websiteUrl = user?.website?.trim() || undefined
 // Transferred-away leads are read-only (matches the speed dial behaviour)
 const readOnly = isTransferred === false

 const sendEmail = (prefill?: { subject?: string; body?: string }) =>
  updateSendEmailWidget({
   to: lead?.email,
   widgetFrom: 'lead',
   show: true,
   prefill,
  })

 const sendSms = (prefill?: { body?: string }) =>
  updateSendSMSWidget({
   to: lead?.phone,
   widgetFrom: 'lead',
   show: true,
   prefill,
  })

 const copyWebsiteLink = async () => {
  if (!websiteUrl) return
  try {
   await navigator.clipboard.writeText(websiteUrl)
   globalToaster({ type: 'success', content: 'Link copied' })
  } catch {
   globalToaster({ type: 'error', content: 'Unable to copy link' })
  }
 }

 return {
  // Lead name highlighted in the message; plain fallback when unnamed
  leadName: (atStart = false): ReactNode =>
   lead?.name ? (
    <strong className="lead-activity-empty-name">{lead.name}</strong>
   ) : (
    `${atStart ? 'This' : 'this'} lead`
   ),
  hasPhone: !!lead?.phone,
  hasEmail: !!lead?.email,
  canSms,
  canCall,
  websiteUrl,
  readOnly,
  sendEmail,
  sendSms,
  call: () => onCall?.(),
  sendWebsiteEmail: () =>
   websiteUrl && sendEmail(websiteEmailPrefill(websiteUrl)),
  sendWebsiteSms: () => websiteUrl && sendSms(websiteSmsPrefill(websiteUrl)),
  copyWebsiteLink,
 }
}

// Font Awesome icons for the actions (same as the lead speed dial)
export const ACTION_ICONS = {
 email: 'fas fa-envelope',
 sms: 'fas fa-comment-alt',
 call: 'fas fa-phone-alt',
 note: 'fas fa-clipboard',
 website: 'fas fa-globe',
 copy: 'fas fa-copy',
 integration: 'fas fa-plug',
}

// Button that can be shown disabled with an explanatory tooltip
export const EmptyStateAction: FC<{
 label: string
 onClick?: () => void
 disabledReason?: string
 // Font Awesome class, e.g. ACTION_ICONS.email
 icon?: string
}> = ({ label, onClick, disabledReason, icon }) => {
 const btn = (
  <Button shape="round" disabled={!!disabledReason} onClick={onClick}>
   {icon && <i className={`${icon} me-2`} />}
   {label}
  </Button>
 )
 // Wrap in span so the tooltip still fires on a disabled button
 return disabledReason ? (
  <Tooltip title={disabledReason}>
   <span className="d-inline-block">{btn}</span>
  </Tooltip>
 ) : (
  btn
 )
}

export const IntegrationAction: FC<{ label: string }> = ({ label }) => {
 const navigate = useNavigate()
 return (
  <EmptyStateAction
   label={label}
   icon={ACTION_ICONS.integration}
   onClick={() => navigate(INTEGRATION_PATH)}
  />
 )
}

// "Send Website Link" — opens the Email / SMS / Copy choice
export const WebsiteLinkDropdown: FC = () => {
 const {
  websiteUrl,
  canSms,
  hasPhone,
  sendWebsiteEmail,
  sendWebsiteSms,
  copyWebsiteLink,
 } = useLeadActivityActions()

 if (!websiteUrl) {
  return (
   <EmptyStateAction
    label="Send Website Link"
    icon={ACTION_ICONS.website}
    disabledReason="Add your website in your profile"
   />
  )
 }

 const menu = (
  <Menu
   items={[
    {
     key: 'email',
     label: 'Send via Email',
     icon: <i className={ACTION_ICONS.email} />,
     onClick: sendWebsiteEmail,
    },
    ...(canSms
     ? [
        {
         key: 'sms',
         label: 'Send via SMS',
         icon: <i className={ACTION_ICONS.sms} />,
         onClick: sendWebsiteSms,
         disabled: !hasPhone,
        },
       ]
     : []),
    {
     key: 'copy',
     label: 'Copy Link',
     icon: <i className={ACTION_ICONS.copy} />,
     onClick: copyWebsiteLink,
    },
   ]}
  />
 )
 return (
  <Dropdown overlay={menu} trigger={['click']}>
   <Button shape="round">
    <i className={`${ACTION_ICONS.website} me-2`} />
    Send Website Link <i className="fas fa-chevron-down ms-1" />
   </Button>
  </Dropdown>
 )
}

// Presentational empty state: icon + headline + body + action buttons
const ActivityEmptyState: FC<{
 icon?: ReactNode
 headline: string
 body?: ReactNode
 actions?: ReactNode
}> = ({ icon, headline, body, actions }) => {
 const { readOnly } = useLeadActivityActions()
 return (
  <div className="no-data-empty-compo lead-activity-empty">
   <Empty
    image={icon}
    description={<span className="no-data-heading">{headline}</span>}
   >
    {body && <p className="no-data-description">{body}</p>}
    {actions && !readOnly && (
     <Space wrap size={[8, 8]} className="justify-content-center mt-2">
      {actions}
     </Space>
    )}
   </Empty>
  </div>
 )
}

export default ActivityEmptyState
